import React, { useMemo, useState } from 'react';

import { styled, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import UsersAdminToolbar from './UsersAdminToolbar';
import { useTablePagination, TablePagination, PaginationContainer } from '../../../components/TablePagination';
import { UsersRecord } from '../../../types/apiTypes';
import AdminUsersContainer from '../AdminUsersContainer';
import UserControl from '../components/UserControl';
import { UserRole } from '../constants/user-roles';

const StyledTable = styled(Table)`
    margin-bottom: 2rem;
`;

const UsersTable: React.FC = () => {
    const { users } = useContainer(AdminUsersContainer);

    const { t } = useTranslation('portal');
    const [searchText, setSearchText] = useState('');
    const [roleFilter, setRoleFilter] = useState<UserRole | null>(null);

    const filteredUsers = useMemo(() => {
        if (!searchText && !roleFilter) return users;

        const normalizedSearch = searchText.toLocaleLowerCase();
        const filtered: UsersRecord[] = [];

        for (let i = 0; i < users.length; i++) {
            const user = users[i];

            if (roleFilter === null || user.roles.includes(roleFilter)) {
                let includedInEmail = false;
                let includedInUsername = false;

                if (user.user_name) {
                    const normalizedUsername = user.user_name.toLocaleLowerCase();
                    includedInUsername = normalizedUsername.includes(normalizedSearch);
                }

                if (user.email) {
                    const normalizedEmail = user.email.toLocaleLowerCase();
                    includedInEmail = normalizedEmail.includes(normalizedSearch);
                }

                if (includedInUsername || includedInEmail) {
                    filtered.push(user);
                }
            }
        }

        return filtered;
    }, [searchText, roleFilter, users]);

    const { onRowsPerPageChange, paginatedValues, onPageChange, rowsPerPage, setPage, count, page } =
        useTablePagination(filteredUsers);

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
        setPage(1);
    };

    const handleChangeRoleFilter = (role: UserRole) => {
        setRoleFilter(role);
    };

    const renderTable = () => {
        if (users.length > 0) {
            return (
                <StyledTable>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('users.username')}</TableCell>
                            <TableCell>{t('users.email')}</TableCell>
                            <TableCell>{t('users.roles')}</TableCell>
                            <TableCell>{t('users.user-actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedValues.map((user) => (
                            <UserControl key={user.user_id} user={user} />
                        ))}
                    </TableBody>
                </StyledTable>
            );
        }

        return null;
    };

    return (
        <>
            <UsersAdminToolbar
                handleChangeSearch={handleChangeSearch}
                handleChangeRoleFilter={handleChangeRoleFilter}
                searchText={searchText}
                filteredCount={count}
                fullCount={users.length}
            />
            {renderTable()}
            <PaginationContainer>
                <TablePagination
                    count={count}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                />
            </PaginationContainer>
        </>
    );
};

export default UsersTable;
